import { useState } from 'react';
import { cn } from '../../lib/utils';

interface Tab {
  id: string;
  label: React.ReactNode;
  content?: React.ReactNode;
  disabled?: boolean;
}

interface TabsProps {
  tabs: Tab[];
  defaultTab?: string;
  activeTab?: string;
  onChange?: (tabId: string) => void;
  variant?: 'default' | 'pills' | 'underline';
  className?: string;
  tabsClassName?: string;
  contentClassName?: string;
}

function Tabs({
  tabs,
  defaultTab,
  activeTab: controlledActiveTab,
  onChange,
  variant = 'default',
  className,
  tabsClassName,
  contentClassName,
}: TabsProps) {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || tabs[0]?.id);

  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;

  const handleTabChange = (tabId: string) => {
    if (controlledActiveTab === undefined) {
      setInternalActiveTab(tabId);
    }
    if (onChange) {
      onChange(tabId);
    }
  };

  const variantClasses = {
    default: {
      tabs: 'border-b border-gray-200 dark:border-gray-800',
      tab: (isActive: boolean) =>
        cn(
          'py-2.5 px-4 text-sm font-medium border-b-2 -mb-px transition-all duration-200 ease-smooth',
          isActive
            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
        ),
    },
    pills: {
      tabs: 'space-x-1.5 p-1 bg-gray-100 dark:bg-gray-800/60 rounded-xl',
      tab: (isActive: boolean) =>
        cn(
          'py-2 px-4 text-sm font-medium rounded-lg transition-all duration-200 ease-smooth',
          isActive
            ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-300 shadow-soft'
            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
        ),
    },
    underline: {
      tabs: 'space-x-8',
      tab: (isActive: boolean) =>
        cn(
          'py-4 px-1 text-sm font-medium border-b-2 transition-all duration-200 ease-smooth',
          isActive
            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
        ),
    },
  };

  const hasContent = tabs.some(tab => tab.content !== undefined);

  return (
    <div className={cn('w-full', className)}>
      <div className={cn('flex', variantClasses[variant].tabs, tabsClassName)}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => !tab.disabled && handleTabChange(tab.id)}
            className={cn(
              variantClasses[variant].tab(activeTab === tab.id),
              tab.disabled && 'opacity-50 cursor-not-allowed'
            )}
            disabled={tab.disabled}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {hasContent && (
        <div className={cn('mt-4 animate-fade-in', contentClassName)}>
          {tabs.find((tab) => tab.id === activeTab)?.content}
        </div>
      )}
    </div>
  );
}

export default Tabs;
